/**
 * PWA Refresh Watchdog v1.0.0
 *
 * Addresses "refresh hang" issues on installed Android PWA where:
 * 1. Refresh takes too long and UI never updates until user switches apps
 * 2. New SW is installed but page stays stale
 *
 * The watchdog:
 * - Monitors time since navigation/refresh start
 * - After timeout, triggers SW update + controlled reload
 * - On visibility/focus return, revalidates queries and forces re-render
 */

import { logger } from '@/lib/logger';

// Increased from 8s to 12s to reduce false triggers on slow networks/cold starts
const WATCHDOG_TIMEOUT_MS = 12000;
const WATCHDOG_FLAG_KEY = 'llx:refresh_watchdog_fired';
const RELOAD_GUARD_KEY = 'llx:reload_in_progress';
const RELOAD_COOLDOWN_MS = 5000;
const COLD_START_KEY = 'llx:last_activity';
const COLD_START_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

let watchdogTimer: number | undefined;
let startTime: number | undefined;

/**
 * Check if a reload is in progress (prevents loops)
 */
const isReloadInProgress = (): boolean => {
  const ts = sessionStorage.getItem(RELOAD_GUARD_KEY);
  if (!ts) return false;
  return Date.now() - parseInt(ts, 10) < RELOAD_COOLDOWN_MS;
};

const markReloadInProgress = () => {
  sessionStorage.setItem(RELOAD_GUARD_KEY, Date.now().toString());
};

/**
 * Check if this is a cold start (app was backgrounded for >5 min)
 * Cold starts get extra tolerance
 */
const isColdStart = (): boolean => {
  const lastActivity = sessionStorage.getItem(COLD_START_KEY);
  if (!lastActivity) return true;
  return Date.now() - parseInt(lastActivity, 10) > COLD_START_THRESHOLD_MS;
};

/**
 * Mark activity timestamp
 */
export const markActivity = () => {
  sessionStorage.setItem(COLD_START_KEY, Date.now().toString());
};

/**
 * Start the refresh watchdog - call on navigation start or page load
 */
export const startRefreshWatchdog = () => {
  // Clear any existing watchdog
  if (watchdogTimer) {
    window.clearTimeout(watchdogTimer);
  }

  startTime = performance.now();
  
  // Cold starts get extra 4s tolerance (16s total)
  const timeout = isColdStart() ? WATCHDOG_TIMEOUT_MS + 4000 : WATCHDOG_TIMEOUT_MS;

  watchdogTimer = window.setTimeout(async () => {
    const elapsed = performance.now() - (startTime ?? 0);
    logger.warn(`[RefreshWatchdog] Timeout after ${Math.round(elapsed)}ms - triggering recovery`);

    // Never auto-reload while offline — a reload can't recover the network
    // and will only loop the splash screen. The stalled UI + Retry button
    // in BootstrapGate is the correct UX for this case.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      logger.log('[RefreshWatchdog] Offline — skipping recovery reload');
      return;
    }

    // Prevent loops
    if (isReloadInProgress()) {
      logger.log('[RefreshWatchdog] Reload already in progress, skipping');
      return;
    }

    // Mark that watchdog fired (for dev logging)
    sessionStorage.setItem(WATCHDOG_FLAG_KEY, 'true');

    // Try to activate waiting SW and reload
    await triggerSWRecovery();
  }, timeout);
};

/**
 * Stop the watchdog (call when navigation completes successfully)
 */
export const stopRefreshWatchdog = () => {
  if (watchdogTimer) {
    window.clearTimeout(watchdogTimer);
    watchdogTimer = undefined;
  }

  const elapsed = startTime ? Math.round(performance.now() - startTime) : 0;
  if (import.meta.env.DEV && startTime) {
    logger.log(`[RefreshWatchdog] Navigation complete in ${elapsed}ms`);
  }

  startTime = undefined;

  // Clear watchdog flag if we completed successfully
  sessionStorage.removeItem(WATCHDOG_FLAG_KEY);
  
  // Mark activity for cold start detection
  markActivity();
};

/**
 * Trigger SW recovery: update registration, activate waiting SW, reload
 */
const triggerSWRecovery = async () => {
  if (!('serviceWorker' in navigator)) {
    logger.log('[RefreshWatchdog] No SW support, reloading directly');
    markReloadInProgress();
    window.location.reload();
    return;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();

    if (registration) {
      // Force update check
      await registration.update();

      // If there's a waiting SW, activate it
      if (registration.waiting) {
        logger.log('[RefreshWatchdog] Activating waiting SW');
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    }

    // Controlled reload after brief delay for SW activation
    await new Promise(r => setTimeout(r, 100));
    markReloadInProgress();
    window.location.reload();
  } catch (err) {
    logger.error('[RefreshWatchdog] Recovery failed:', err);
    // Last resort: hard reload
    markReloadInProgress();
    window.location.reload();
  }
};

/**
 * Check if watchdog fired in this session (for dev logging)
 */
export const didWatchdogFire = (): boolean => {
  return sessionStorage.getItem(WATCHDOG_FLAG_KEY) === 'true';
};
